import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import CheckBoxHookForm from "../../../../components/CheckBoxHookForm";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import {
  isRefetchExpandedModal,
  producerCodeMastercarrierId,
  rowForEditProdCode,
} from "../atom";
import useAddProdCode from "../hooks/useAddProdCode";
import useEditProdCode from "../hooks/useEditProdCode";
import useSendEmail from "../hooks/useSendEmail";
import { IProducerCodes } from "../types";
import ProducerCodeDetails from "./ProducerCodeDetails";

const getDerivedStatus = (rowForEdit, isAddState) => {
  if (isAddState) return true;
  if (rowForEdit) {
    if (rowForEdit.isSuspended) return 1;
    return rowForEdit.isActive === true ? true : "false";
  }

  return true;
};

export const ProducerCodeForm = ({
  setIsAddState,
  isAddState,
  setIsEditState,
  isEditState,
  refetchGetGrpProdCode,
}) => {
  const queryClient = useQueryClient();
  const [newData, setNewData] = useState<IProducerCodes | undefined>();
  const [saveModal, setSaveModal] = useState(false);
  const setMasterCarrierId = useSetRecoilState(producerCodeMastercarrierId);
  const setIsRefetchModal = useSetRecoilState(isRefetchExpandedModal);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditProdCode);

  const user = useRecoilValue(accountAtom);
  const franchise = !user?.isNonFranchise && user?.isFranchise;
  const getDerivedAssociatedTo = () =>
    isAddState ? (franchise ? "Office" : null) : rowForEdit?.associatedTo;
  const getDerivedIsFranchise = () =>
    isAddState ? franchise : rowForEdit?.isFranchise;

  const useFormMethods = useForm<IProducerCodes>({
    defaultValues: {
      ...rowForEdit,
      status: getDerivedStatus(rowForEdit, isAddState),
      isActive: getDerivedStatus(rowForEdit, isAddState) === true,
      isFranchise: getDerivedIsFranchise(),
      associatedTo: getDerivedAssociatedTo(),
    },
  });
  const [isSaving, setIsSaving] = useState(false);
  const {
    formState: { isDirty },
    setValue,
    register,
  } = useFormMethods;

  const handleCancel = () => {
    setIsAddState(false);
    setIsEditState(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setMasterCarrierId(undefined);
  };

  const onSubmit = (data: IProducerCodes) => {
    setNewData({ ...data });
    if (isAddState) {
      setSaveModal(true);
    } else if (isEditState && isDirty) {
      setSaveModal(true);
    } else {
      setSaveModal(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };

  const { mutate: addNewProdCode } = useAddProdCode();
  const { mutate: editProdCode } = useEditProdCode();

  const { mutateAsync: sendProdCodeEmail } = useSendEmail();

  const saveNewProducerCode = (data: IProducerCodes | undefined = newData) => {
    if (isAddState) {
      addNewProdCode(
        { ...data },
        {
          onSuccess: (res) => {
            if (res.status === 201 || res.status === 200) {
              sendProdCodeEmail(res.data, {
                onSuccess: (res) => {
                  if (res) {
                    refetchGetGrpProdCode().then((result) => {
                      if (result.status === 400 && result.data) {
                        toast.fire(
                          "Success",
                          "New Producer Code Added Successfully, but failed in sending email",
                          "warning",
                        );
                        setIsSaving(false);
                      } else {
                        toast.fire(
                          "Success",
                          "New Producer Code Added Successfully",
                          "success",
                        );
                      }
                      queryClient.invalidateQueries("ProducerCode");
                      handleCancel();
                      setIsRefetchModal(true);
                      setIsAddState(false);
                      setIsEditState(false);
                      setSaveModal(false);
                      setIsSaving(false);
                    });
                  }
                },
              });
            } else if (
              res.status === 204 ||
              res.data === "duplicate producer code"
            ) {
              toast.fire(
                "Failed ",
                "Failed to Add Producer code! Already Exist.",
                "error",
              );
              setIsSaving(false);
            } else {
              toast.fire("Error", "Saving Failed: Server Error", "error");
              setIsSaving(false);
            }
          },
          onError: (error: any) => {
            if (
              error.response.data.message === "Failed sending of email" ||
              error.response.data.message === "Failed inserting of notification"
            ) {
              toast.fire(
                "Add Producer Code",
                "New Producer Code added successfully, but " +
                  error.response.data.message,
                "warning",
              );
              setIsAddState(false);
              setMasterCarrierId(undefined);
              setSaveModal(false);
              setIsSaving(false);
            } else {
              toast.fire(
                "Adding new Producer Code  failed",
                "Server Error: Cannot add new Producer Code",
                "error",
              );
              setIsSaving(false);
            }
          },
        },
      );
    } else if (isEditState) {
      editProdCode(
        { ...data },
        {
          onSuccess: (res) => {
            if (res.status === 201 || res.status === 200) {
              sendProdCodeEmail(res.data, {
                onSuccess: (res) => {
                  if (res) {
                    refetchGetGrpProdCode().then((result) => {
                      if (result.status === 400 && result.data) {
                        toast.fire(
                          "Success",
                          "Producer Code Updated Successfully, but failed in sending email",
                          "warning",
                        );
                        setIsSaving(false);
                      } else {
                        toast.fire(
                          "Success",
                          "Producer Code Updated Successfully",
                          "success",
                        );
                      }
                      queryClient.invalidateQueries("ProducerCode");

                      handleCancel();
                      setIsAddState(false);
                      setIsEditState(false);
                      setSaveModal(false);
                      setIsSaving(false);
                    });
                  }
                },
              });
            } else if (res.data === "No Data Changes") {
              toast.fire(
                "Saving Failed",
                "No Changes have been made",
                "warning",
              );
              setIsSaving(false);
            } else if (
              res.status === 400 ||
              res.status === 204 ||
              res.data === "duplicate producer code"
            ) {
              toast.fire(
                "Failed ",
                "Failed to Update Producer code! Already Exist.",
                "error",
              );
              setIsSaving(false);
            } else {
              toast.fire("Error", "Saving Failed: Server Error", "error");
              setIsSaving(false);
            }
          },
          onError: (error: any) => {
            if (
              error.response.data.message === "Failed sending of email" ||
              error.response.data.message === "Failed inserting of notification"
            ) {
              toast.fire(
                "Update Producer Code",
                "Producer Code updated successfully, but " +
                  error.response.data.message,
                "warning",
              );
              setIsAddState(false);
              setMasterCarrierId(undefined);
              setSaveModal(false);
              setIsSaving(false);
            } else {
              toast.fire(
                "Updated Producer Code  failed",
                "Server Error: Cannot update Producer Code",
                "error",
              );
              setIsSaving(false);
            }
          },
        },
      );
    }
  };

  return (
    <div className="relative">
      <div className="divide-y">
        <div className="px-4 pb-8 border-l-2 border-primary  box-shadow ">
          <FormProvider {...useFormMethods}>
            <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
              <div className="flex items-center gap-x-2 text-quinary font-bold font-sans text-lg py-2 border-b-2 border-secondary">
                <div className="text-base text-primary font-bold">
                  <label>
                    {isAddState ? "Add Producer Code" : "Edit Producer Code"}
                  </label>
                </div>
                <SubmitHookForm
                  onClick={undefined}
                  type="submit"
                  buttonText={isSaving ? "Saving..." : "Save"}
                  disabled={isSaving}
                />
                <CancelButton
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
              <div className="flex justify-between my-3">
                <div className="text-xs text-secondary italic font-normal">
                  <b> Note:</b> All fields with * are required to fill.
                </div>
                <div>
                  {user.isFranchise && user.isNonFranchise && (
                    <div className="flex flex-row items-center gap-2">
                      <label className="flex text-xs lg:justify-end sm:justify-start">
                        Franchise:
                      </label>
                      <CheckBoxHookForm
                        name="isFranchise"
                        type="checkbox"
                        register={register}
                        validation={{
                          required: {
                            value: false,
                            message: "Required Field",
                          },
                        }}
                        className="w-4 h-4"
                        label="isFranchise"
                        onClick={(e) => {
                          if (e === null || e !== null) {
                            setValue("distributionChannel", "");
                            setValue("officeCode", null);
                            setValue("producer", "");
                            setValue("issuedTo", "");
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>
            <div>
              <ProducerCodeDetails
                rowForEdit={rowForEdit}
                isAddState={isAddState}
              />
            </div>
          </FormProvider>
        </div>
        <div className="hidden sm:block" aria-hidden="true"></div>
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Producer Code" : "Edit Producer Code"}
          qConfirmation={
            isAddState
              ? " Are you sure you want to add this data?"
              : " Are you sure you want to update this data?"
          }
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            saveNewProducerCode(newData);
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      </div>
    </div>
  );
};
